import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useTranslation } from 'react-i18next';

// UI Components
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../ui/form';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { useToast } from '../../hooks/use-toast';

// Icons
import {
  ArrowLeft,
  ArrowRight,
  User,
  Home,
  GraduationCap,
  Users,
  MapPin,
  Phone,
  Mail,
  Calendar,
  Car,
  CheckCircle,
  AlertCircle,
  Loader2,
} from 'lucide-react';

// Hooks
import { useStudentFormData } from '../../hooks/useStudents';

// Validation schema for comprehensive student registration
const studentRegistrationSchema = z.object({
  // Personal Information
  first_name: z.string()
    .min(2, 'First name must be at least 2 characters')
    .max(50, 'First name cannot exceed 50 characters')
    .regex(/^[a-zA-ZÀ-ÿ\u0100-\u017F\s'-]+$/, 'Please enter a valid name (letters, spaces, hyphens, and apostrophes only)'),
  last_name: z.string()
    .min(2, 'Last name must be at least 2 characters')
    .max(50, 'Last name cannot exceed 50 characters')
    .regex(/^[a-zA-ZÀ-ÿ\u0100-\u017F\s'-]+$/, 'Please enter a valid name (letters, spaces, hyphens, and apostrophes only)'),
  birthday: z.string()
    .min(1, 'Date of birth is required')
    .refine((dateString) => {
      const birthDate = new Date(dateString);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      // Calculate exact age
      const exactAge = monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())
        ? age - 1
        : age;

      return exactAge >= 10 && exactAge < 100;
    }, 'Student must be between 10 and 99 years old')
    .refine((dateString) => {
      const birthDate = new Date(dateString);
      const today = new Date();
      return birthDate <= today;
    }, 'Date of birth cannot be in the future'),
  gender: z.enum(['M', 'F', 'O'], { required_error: 'Gender is required' }),
  
  // Address Information
  address_1: z.string().min(1, 'Address line 1 is required'),
  address_2: z.string().optional(),
  address_3: z.string().optional(), 
  address_4: z.string().optional(),
  eircode: z.string().optional(),
  home_county: z.string().min(1, 'Home county is required'),
  
  // Parent/Guardian Information
  parent_name: z.string()
    .max(100, 'Parent/Guardian name cannot exceed 100 characters')
    .regex(/^$|^[a-zA-ZÀ-ÿ\u0100-\u017F\s'-]+$/, 'Please enter a valid name (letters, spaces, hyphens, and apostrophes only)')
    .optional()
    .or(z.literal('')),
  parent_email: z.string().email('Invalid parent email address'),
  parent_phone: z.string()
    .min(1, 'Parent/Guardian phone is required')
    .regex(/^[\+]?[0-9\s\-\(\)]{7,15}$/, 'Please enter a valid phone number (7-15 digits, may include +, spaces, -, (, ))'),
  
  // Academic Information
  school: z.string().optional(),
  school_year: z.string().min(1, 'School year is required'),
  course: z.string().min(1, 'Course selection is required'),
  
  // Previous Attendance
  attended_before: z.boolean(),
  when_attended: z.string().optional(),

  // Leadership Role
  is_cinnire: z.boolean(),

  // Travel and Accommodation
  travel_required: z.boolean(),
  travel_location: z.string().optional(),
  has_friends: z.boolean(),
  
  // Additional Information
  notes: z.string().optional(),
}).refine(
  (data) => {
    // If attended_before is true, when_attended must be provided
    if (data.attended_before && !data.when_attended?.trim()) {
      return false;
    }
    return true;
  },
  {
    message: 'Please specify when you previously attended',
    path: ['when_attended'],
  }
).refine(
  (data) => {
    // If travel_required is true, travel_location should be provided
    if (data.travel_required && !data.travel_location?.trim()) {
      return false;
    }
    return true;
  },
  {
    message: 'Please select a travel location',
    path: ['travel_location'],
  }
);

const StudentRegistrationForm = ({
  open,
  onOpenChange,
  onSubmit,
  isLoading = false
}) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(1);
  const [isManualSubmission, setIsManualSubmission] = useState(false);

  // Registration steps with translations
  const REGISTRATION_STEPS = [
    { id: 1, title: t('studentReg.personalInfo'), icon: User },
    { id: 2, title: t('studentReg.addressInfo'), icon: MapPin },
    { id: 3, title: t('studentReg.parentContact'), icon: Phone },
    { id: 4, title: t('studentReg.academicInfo'), icon: GraduationCap },
    { id: 5, title: t('studentReg.travelInfo'), icon: Car },
    { id: 6, title: t('studentReg.reviewSubmit'), icon: CheckCircle },
  ];

  // Track step changes for debugging
  useEffect(() => {
    // Step navigation tracking removed for production
  }, [currentStep]);
  const { toast } = useToast();

  // Form dependencies
  const {
    schoolYears,
    courses,
    travelLocations,
    isLoading: formDataLoading
  } = useStudentFormData();

  // Debug logging (only on errors, not every render)
  // Removed excessive logging - only log on actual errors

  const form = useForm({
    resolver: zodResolver(studentRegistrationSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      birthday: '',
      gender: '',
      address_1: '',
      address_2: '',
      address_3: '',
      address_4: '',
      eircode: '',
      home_county: '',
      parent_name: '',
      parent_email: '',
      parent_phone: '',
      school: '',
      school_year: '',
      course: '',
      attended_before: false,
      when_attended: '',
      is_cinnire: false,
      travel_required: false,
      travel_location: '',
      has_friends: false,
      notes: '',
    },
  });

  // Reset form when dialog closes
  const handleDialogChange = (newOpen) => {
    if (!newOpen) {
      // Dialog is closing - reset form and step
      form.reset();
      setCurrentStep(1);
      setIsManualSubmission(false);
    }
    onOpenChange(newOpen);
  };

  const handleNext = async () => {
    const stepFields = getStepFields(currentStep);
    const isValid = await form.trigger(stepFields);

    if (isValid) {
      const newStep = Math.min(currentStep + 1, REGISTRATION_STEPS.length);
      // Step navigation: moving to next step
      setCurrentStep(newStep);
    } else {
      toast({
        title: 'Please complete required fields',
        description: 'Some information is missing or invalid.',
        variant: 'destructive',
      });
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (data) => {
    try {
      await onSubmit?.(data);
      toast({
        title: 'Registration successful',
        description: `${data.first_name} ${data.last_name} has been registered successfully.`,
        variant: 'success',
      });
      form.reset();
      setCurrentStep(1);
      setIsManualSubmission(false);
      onOpenChange(false);
    } catch (error) {
      toast({
        title: 'Registration failed',
        description: error.message || 'Something went wrong. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const getStepFields = (step) => {
    switch (step) {
      case 1: return ['first_name', 'last_name', 'birthday', 'gender'];
      case 2: return ['address_1', 'home_county'];
      case 3: return ['parent_name', 'parent_email', 'parent_phone'];
      case 4: return ['school_year', 'course'];
      case 5: return ['travel_required', 'travel_location'];
      default: return [];
    }
  };

  const getCurrentStepTitle = () => {
    return REGISTRATION_STEPS.find(step => step.id === currentStep)?.title || '';
  };

  const renderPersonalInformation = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="first_name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('studentReg.firstName')} *</FormLabel>
              <FormControl>
                <Input placeholder={t('studentReg.placeholders.firstName')} autoComplete="off" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="last_name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('studentReg.lastName')} *</FormLabel>
              <FormControl>
                <Input placeholder={t('studentReg.placeholders.lastName')} autoComplete="off" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="birthday"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('studentReg.dateOfBirth')} *</FormLabel>
              <FormControl>
                <Input type="date" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="gender"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('studentReg.gender')} *</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={t('studentReg.gender')} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="M">{t('studentReg.male')}</SelectItem>
                  <SelectItem value="F">{t('studentReg.female')}</SelectItem>
                  <SelectItem value="O">{t('studentReg.other')}</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );

  const renderAddressInformation = () => (
    <div className="space-y-4">
      <FormField
        control={form.control}
        name="address_1"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('studentReg.address')} *</FormLabel>
            <FormControl>
              <Input placeholder={t('studentReg.placeholders.address1')} autoComplete="off" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="address_2"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('studentReg.address2')}</FormLabel>
            <FormControl>
              <Input placeholder={t('studentReg.placeholders.address2')} autoComplete="off" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="address_3"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('studentReg.address3')}</FormLabel>
              <FormControl>
                <Input placeholder={t('studentReg.placeholders.address3')} autoComplete="off" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="address_4"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('studentReg.address4')}</FormLabel>
              <FormControl>
                <Input placeholder={t('studentReg.placeholders.address4')} autoComplete="off" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="home_county"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('studentReg.county')} *</FormLabel>
              <FormControl>
                <Input placeholder={t('studentReg.placeholders.county')} autoComplete="off" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="eircode"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('studentReg.eircode')}</FormLabel>
              <FormControl>
                <Input placeholder={t('studentReg.placeholders.eircode')} autoComplete="off" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );

  const renderParentInformation = () => (
    <div className="space-y-4">
      <FormField
        control={form.control}
        name="parent_name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('studentReg.parentName')}</FormLabel>
            <FormControl>
              <Input
                placeholder={t('studentReg.placeholders.parentName')}
                autoComplete="off"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="parent_email"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('studentReg.parentEmail')} *</FormLabel>
            <FormControl>
              <Input
                placeholder={t('studentReg.placeholders.parentEmail')}
                type="email"
                autoComplete="off"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="parent_phone"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('studentReg.parentPhone')} *</FormLabel>
            <FormControl>
              <Input
                placeholder={t('studentReg.placeholders.parentPhone')}
                autoComplete="off"
                {...field}
              />
            </FormControl>
            <FormDescription>
              <strong>Accepted formats:</strong><br />
              • International: +353 87 123 4567<br />
              • Mobile: 087 123 4567<br />
              • Landline: 01 234 5678
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );

  const renderAcademicInformation = () => (
    <div className="space-y-4">
      <FormField
        control={form.control}
        name="school"
        render={({ field }) => (
          <FormItem>
            <FormLabel>School Name</FormLabel>
            <FormControl>
              <Input placeholder={t('studentReg.placeholders.school')} autoComplete="off" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="school_year"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('studentReg.schoolYear')} *</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value} disabled={formDataLoading}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder={formDataLoading ? t('studentReg.placeholders.loadingSchoolYears') : t('studentReg.placeholders.selectSchoolYear')} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {formDataLoading ? (
                  <div className="flex items-center justify-center py-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </div>
                ) : (
                  schoolYears.map(year => (
                    <SelectItem key={year.uuid} value={year.uuid}>{year.year}</SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="course"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('studentReg.course')} *</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value} disabled={formDataLoading}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder={formDataLoading ? t('studentReg.placeholders.loadingCourses') : t('studentReg.placeholders.selectCourse')} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {formDataLoading ? (
                  <div className="flex items-center justify-center py-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </div>
                ) : (
                  courses.map(course => (
                    <SelectItem key={course.uuid} value={course.uuid} disabled={course.has_archived_students}>
                      {course.name}
                      {course.has_archived_students && ` (${t('courses.archive.archivedBadge', 'Archived')})`}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="pt-4 border-t">
        <FormField
          control={form.control}
          name="attended_before"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>Has the student attended Coláiste Lurgan before?</FormLabel>
                <FormDescription>
                  Select this if the student has previously attended any course at Coláiste Lurgan
                </FormDescription>
              </div>
            </FormItem>
          )}
        />

        {form.watch('attended_before') && (
          <FormField
            control={form.control}
            name="when_attended"
            render={({ field }) => (
              <FormItem className="mt-4">
                <FormLabel>When did the student previously attend? *</FormLabel>
                <FormControl>
                  <Input placeholder={t('studentReg.placeholders.whenAttended')} autoComplete="off" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </div>

      <div className="pt-4 border-t">
        <FormField
          control={form.control}
          name="is_cinnire"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>Is this student a Cinnire (Leader)?</FormLabel>
                <FormDescription>
                  Select this if the student will have a leadership role during the course
                </FormDescription>
              </div>
            </FormItem>
          )}
        />
      </div>
    </div>
  );

  const renderTravelAndAccommodation = () => (
    <div className="space-y-4">
      <FormField
        control={form.control}
        name="travel_required"
        render={({ field }) => (
          <FormItem className="flex flex-row items-start space-x-3 space-y-0">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel>Does the student require travel arrangements?</FormLabel>
              <FormDescription>
                We can arrange transport from various pickup points across Ireland
              </FormDescription>
            </div>
          </FormItem>
        )}
      />

      {form.watch('travel_required') && (
        <FormField
          control={form.control}
          name="travel_location"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('studentReg.travelLocation')} *</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value} disabled={formDataLoading}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={formDataLoading ? t('studentReg.placeholders.loadingLocations') : t('studentReg.placeholders.selectTravelLocation')} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {formDataLoading ? (
                    <div className="flex items-center justify-center py-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                    </div>
                  ) : (
                    travelLocations.map(location => (
                      <SelectItem key={location.uuid} value={location.uuid}>{location.name}</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      <FormField
        control={form.control}
        name="notes"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Additional Notes</FormLabel>
            <FormControl>
              <Textarea
                placeholder="Any additional information, special requirements, or notes..."
                className="resize-none"
                rows={4}
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );

  const renderReviewAndSubmit = () => {
    // Use getValues instead of watch to avoid triggering re-renders
    const watchedValues = form.getValues();
    const selectedCourse = courses.find(c => c.uuid === watchedValues.course);
    const selectedSchoolYear = schoolYears.find(sy => sy.uuid === watchedValues.school_year);
    const selectedTravelLocation = travelLocations.find(tl => tl.uuid === watchedValues.travel_location);

    return (
      <div className="space-y-6">
        <div className="text-center mb-6">
          <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-2" />
          <h3 className="text-lg font-semibold">Review Your Information</h3>
          <p className="text-muted-foreground">Please review all details before submitting your registration</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Personal Information */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center">
                <User className="h-4 w-4 mr-2" />
                Personal Information
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-2 text-sm">
              <div><strong>Name:</strong> {watchedValues.first_name} {watchedValues.last_name}</div>
              <div><strong>Date of Birth:</strong> {watchedValues.birthday}</div>
              <div><strong>Gender:</strong> {watchedValues.gender === 'M' ? 'Male' : watchedValues.gender === 'F' ? 'Female' : watchedValues.gender || 'Not specified'}</div>
            </CardContent>
          </Card>

          {/* Address */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center">
                <MapPin className="h-4 w-4 mr-2" />
                Address
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-1 text-sm">
              <div>{watchedValues.address_1}</div>
              {watchedValues.address_2 && <div>{watchedValues.address_2}</div>}
              {watchedValues.address_3 && <div>{watchedValues.address_3}</div>}
              <div>{watchedValues.home_county} {watchedValues.eircode}</div>
            </CardContent>
          </Card>

          {/* Parent/Guardian */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center">
                <Phone className="h-4 w-4 mr-2" />
                Parent/Guardian Contact
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-2 text-sm">
              <div><strong>Name:</strong> {watchedValues.parent_name || <span className="text-gray-400">(Optional)</span>}</div>
              <div><strong>Email:</strong> {watchedValues.parent_email}</div>
              <div><strong>Phone:</strong> {watchedValues.parent_phone}</div>
            </CardContent>
          </Card>

          {/* Academic */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center">
                <GraduationCap className="h-4 w-4 mr-2" />
                Academic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-2 text-sm">
              <div><strong>School:</strong> {watchedValues.school || 'Not specified'}</div>
              <div><strong>School Year:</strong> {selectedSchoolYear?.year || watchedValues.school_year}</div>
              <div><strong>Course:</strong> {selectedCourse?.name || watchedValues.course}</div>
              {watchedValues.attended_before && (
                <div><strong>Previously Attended:</strong> {watchedValues.when_attended}</div>
              )}
              {watchedValues.is_cinnire && (
                <div><strong>Leadership Role:</strong> Cinnire (Leader)</div>
              )}
            </CardContent>
          </Card>

          {/* Travel & Additional Information */}
          {(watchedValues.travel_required || watchedValues.notes) && (
            <Card className="md:col-span-2">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center">
                  <Car className="h-4 w-4 mr-2" />
                  Additional Information
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 space-y-2 text-sm">
                {watchedValues.travel_required && (
                  <div><strong>Travel Required:</strong> {selectedTravelLocation?.name || watchedValues.travel_location}</div>
                )}
                {watchedValues.notes && (
                  <div><strong>Notes:</strong> {watchedValues.notes}</div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1: return renderPersonalInformation();
      case 2: return renderAddressInformation();
      case 3: return renderParentInformation();
      case 4: return renderAcademicInformation();
      case 5: return renderTravelAndAccommodation();
      case 6: return renderReviewAndSubmit();
      default: return null;
    }
  };

  // Remove the global loading blocker - let the form load immediately
  // Individual steps will handle their own loading states

  return (
    <Dialog open={open} onOpenChange={handleDialogChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('studentReg.title')} - Coláiste Lurgan</DialogTitle>
          <DialogDescription>
            {t('studentReg.personalInfoDescription')}
          </DialogDescription>
        </DialogHeader>

        {/* Progress Indicator */}
        <div className="mb-6">
          <div className="flex items-center mb-2">
            {REGISTRATION_STEPS.map((step, index) => (
              <React.Fragment key={step.id}>
                <div className={`
                  w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                  ${step.id === currentStep
                    ? 'bg-primary text-primary-foreground'
                    : step.id < currentStep
                    ? 'bg-green-500 text-white'
                    : 'bg-muted text-muted-foreground'
                  }
                `}>
                  {step.id < currentStep ? (
                    <CheckCircle className="h-4 w-4" />
                  ) : (
                    <step.icon className="h-4 w-4" />
                  )}
                </div>
                {index < REGISTRATION_STEPS.length - 1 && (
                  <div className={`
                    flex-1 h-0.5
                    ${step.id < currentStep ? 'bg-green-500' : 'bg-muted'}
                  `} />
                )}
              </React.Fragment>
            ))}
          </div>
          <div className="text-center">
            <Badge variant="outline">
              {t('studentReg.step', { current: currentStep, total: REGISTRATION_STEPS.length })}: {getCurrentStepTitle()}
            </Badge>
          </div>
        </div>

        {/* Form Content */}
        <Form {...form}>
          <form onSubmit={(e) => {
            e.preventDefault();
            // Only proceed if this is a manual submission (user clicked submit button)
            if (!isManualSubmission) {
              return;
            }

            form.handleSubmit(handleSubmit)(e);
          }} className="space-y-6">
            <div key={currentStep}>
              {renderCurrentStep()}
            </div>

            {/* Navigation Buttons */}
            <div className="flex justify-between pt-6 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={handlePrevious}
                disabled={currentStep === 1}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('studentReg.previous')}
              </Button>

              {currentStep < REGISTRATION_STEPS.length ? (
                <Button type="button" onClick={handleNext}>
                  {t('studentReg.next')}
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={isLoading}
                  onClick={() => {
                    setIsManualSubmission(true);
                  }}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t('studentReg.processing')}
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      {t('studentReg.submit')}
                    </>
                  )}
                </Button>
              )}
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default StudentRegistrationForm;